/**
 * Pure spreadsheet → column-table parsing, shared by the manual importer
 * (PImport's useImport hook) and the AI import client tools
 * (AiImportTools). Extracted here so both paths use ONE parser — no drift —
 * and so the logic stays unit-testable without a React host.
 *
 * Output is the column-based {@link TableColumnData} shape the importer's
 * mapping + {@code exportGuests} already consume.
 */
import { TableColumnData } from "@/components/PImport/types/TableColumnData";
import { DataTypeDetectionService } from "@/lib/utils/import-dtype-detection";
import { normalizeFieldValue } from "@/lib/utils/input-validator";
import { isValidTimeZone } from "@/lib/utils/datetime-tz";
import { resolveWallClock } from "@/lib/utils/zonedWallClock";

/** Options of the spreadsheet parsers. */
export type SpreadsheetParseOptions = {
  /**
   * IANA zone of the event the file is imported into. Excel date cells that
   * carry a time are read as WALL CLOCK in this zone (see
   * {@link excelDateCellToIso}). Absent — a surface outside an event scope —
   * keeps the historic UTC reading.
   */
  timeZone?: string;
};

/** ExcelJS' spelling of built-in number format 22 (`m/d/yy h:mm`) — see
 *  {@link numFmtHasTime}. */
const EXCELJS_BUILTIN_22 = 'm/d/yy "h":mm';

/**
 * Does an Excel number format print a TIME? True for `yyyy-mm-dd hh:mm`,
 * `hh:mm:ss`, `h:mm AM/PM`, `[h]:mm` (elapsed); false for `yyyy-mm-dd`,
 * `dd.mm.yyyy`, `mmm-yy`, `[$-407]d. mmmm yyyy`, `mm-dd-yy` (ExcelJS' own
 * default for a Date cell without a format) and for no format at all.
 *
 * Quoted literals (`"Datum: "dd.mm.yyyy`), backslash-escaped characters and
 * `[...]` sections (locale prefixes `[$-407]`, colours `[Red]`, conditions)
 * are stripped first — a `d`, `h` or `s` inside them is text, not a token.
 * The elapsed-time sections `[h]`, `[mm]`, `[ss]` are the one bracket form
 * that IS a time and are checked before the strip. After the strip, `m` alone
 * is ambiguous (month or minute) and only means minute next to an `h` or `s`
 * — so a remaining `h`/`s` (or `AM/PM`, `A/P`) is the whole decision.
 */
export const numFmtHasTime = (fmt?: string | null): boolean => {
  if (typeof fmt !== "string" || fmt.length === 0) return false;
  if (/\[(h+|m+|s+)\]/i.test(fmt)) return true;
  // ExcelJS quirk, checked BEFORE the quote strip: Excel's built-in format
  // id 22 is `m/d/yy h:mm`, but ExcelJS' table spells it `m/d/yy "h":mm`
  // (node_modules/exceljs/lib/xlsx/defaultnumformats.js:23) and hands that
  // string out as `cell.numFmt` for every cell stored with numFmtId 22 — the
  // format Excel itself applies when a user types "5/7/26 14:30". The strip
  // below would eat the quoted `h` and leave `m/d/yy :mm`, a date-only
  // reading of a date-TIME cell. Exact string only: a quoted `h` anywhere
  // else stays text (`"Stunden: "dd.mm.yyyy`).
  if (fmt === EXCELJS_BUILTIN_22) return true;
  const bare = fmt
    .replace(/"[^"]*"/g, "")
    .replace(/\\./g, "")
    .replace(/\[[^\]]*\]/g, "")
    .replace(/[_*]./g, "");
  return /[hs]/i.test(bare) || /am\/pm|a\/p/i.test(bare);
};

/**
 * One Excel date cell → the ISO instant the importer stores.
 *
 * An Excel date is zoneless: ExcelJS hands it over as a `Date` whose UTC
 * fields ARE the numbers the user sees in the sheet. The guest export writes
 * DATE_TIME cells as wall clock in the EVENT zone (19:54 in Berlin, not the
 * 17:54 of the instant), so reading the cell back as UTC would land a
 * re-imported value shifted by the zone offset. Contract, mirror of the
 * exporters':
 *   - no `tz` (the caller drops an invalid one) ⇒ `toISOString()`, the old
 *     behaviour — never the browser's or the server's zone;
 *   - exactly 00:00:00.000 and NOT `timeFormat` ⇒ `toISOString()`: a cell in a
 *     date-only format (or none) showing midnight is a calendar DAY and never
 *     shifts. With `timeFormat` (the cell's number format prints a time —
 *     `yyyy-mm-dd hh:mm`, what both exporters stamp on a DATE_TIME) midnight
 *     is a time like any other: a DATE_TIME at event-wall midnight round-trips
 *     to its instant instead of landing 1–2 h off;
 *   - anything else ⇒ the UTC fields are the wall clock in `tz`, resolved
 *     against the zone's offset ON THAT DAY, seconds + milliseconds kept
 *     (the export keeps them in the serial).
 *
 * Resolution: `asUtc` = the wall fields framed as UTC; candidate =
 * `asUtc − offset(asUtc)`; verified by re-deriving the candidate's FULL wall
 * date-time in the zone. If it differs, the wall time sits next to a DST
 * change (the offset at the guess ≠ the offset at the answer) and it is
 * recomputed once with `offset(candidate)`. A wall time inside a spring-
 * forward gap has no instant at all: neither pass verifies and the LATER of
 * the two candidates wins — the post-jump instant (Berlin 02:30 on the
 * switch day reads as 03:30 CEST = 01:30Z), the usual forward resolution. A
 * wall time in the repeated fall-back hour has two instants; the first pass
 * verifies on one of them.
 *
 * Self-contained by design: `datetime-tz.ts` is kept in sync with
 * website-builder-frontend and its `localMinutesToIso` wraps its correction
 * at ±720 minutes — a whole day off for |offset| ≥ 12 h — so it is not used
 * here (fixed there since PB-TZ-1; still not used because it resolves a gap
 * backward). Resolution lives in zonedWallClock.ts (shared with the
 * create-event Velo tool).
 */
export const excelDateCellToIso = (
  d: Date,
  tz?: string,
  timeFormat = false
): string => {
  if (!tz) return d.toISOString();
  const seconds = d.getUTCSeconds();
  const ms = d.getUTCMilliseconds();
  const minutes = d.getUTCHours() * 60 + d.getUTCMinutes();
  if (!timeFormat && minutes === 0 && seconds === 0 && ms === 0) {
    return d.toISOString();
  }
  // A TIME-only cell ("14:30") is a serial below 1 and arrives dated
  // 1899-12-30: there is no calendar day to resolve an offset for (and the
  // zone's pre-standard-time offset would bend the minutes), so it stays as
  // it always was.
  if (d.getUTCFullYear() < 1900) return d.toISOString();
  const asUtc = d.getTime() - ms;
  return new Date(resolveWallClock(asUtc, tz) + ms).toISOString();
};

/**
 * ExcelJS returns objects for rich text, hyperlinks, formulas, and errors.
 * Without unwrapping, `String(obj)` becomes "[object Object]" downstream.
 * Order matters: prefer the visible text the user sees in Excel over
 * metadata like hyperlink targets — a hyperlink cell's `value.text` is
 * what's displayed; `value.hyperlink` is just the URL.
 */
export const extractCellText = (value: unknown): string | Date | null => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value !== "object") return String(value);
  const v = value as Record<string, unknown>;
  // Rich text directly on the value
  if (Array.isArray(v.richText)) {
    return (v.richText as Array<{ text?: string }>)
      .map((p) => p?.text ?? "")
      .join("");
  }
  // Hyperlink with display text — text may itself be a string OR a
  // nested rich-text object. Recurse so we always end up with a string.
  if (v.text !== undefined) {
    const inner = extractCellText(v.text);
    if (typeof inner === "string" && inner.length > 0) return inner;
  }
  // Formula result wraps the actual value
  if (v.result !== undefined) {
    const inner = extractCellText(v.result);
    if (inner !== null) return inner;
  }
  // Hyperlink-only fallback (no display text → URL is the user's intent)
  if (typeof v.hyperlink === "string") return v.hyperlink;
  if (typeof v.error === "string") return v.error;
  if (typeof v.formula === "string") return "";
  return null;
};

/**
 * Extract table data from an Excel file using ExcelJS.
 * Assumes headers are in the first row and extracts all data rows.
 * Ensures all columns have the same number of values.
 */
export const extractTableDataExcel = async (
  file: File,
  opts?: SpreadsheetParseOptions
): Promise<TableColumnData[]> => {
  // Validated ONCE, not per cell: a broken zone string in the event document
  // must degrade to the UTC reading, not throw out of `Intl` mid-sheet.
  const timeZone = isValidTimeZone(opts?.timeZone) ? opts?.timeZone : undefined;
  const ExcelJS = await import("exceljs");
  const arrayBuffer = await file.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  // Use the first worksheet
  const worksheet = workbook.worksheets[0];

  if (!worksheet || worksheet.rowCount === 0) {
    return [];
  }

  // Convert worksheet to array format. Index by Excel rowNumber so blank
  // rows don't shift the indices — otherwise downstream "row N is invalid"
  // messages drift from what the user sees in Excel.
  const jsonData: unknown[][] = [];
  let maxColCount = 0;
  // The Date cells whose number format prints a TIME (`yyyy-mm-dd hh:mm`):
  // for those, midnight is a time, not a calendar day — see
  // `excelDateCellToIso`. Keyed by object identity: ExcelJS hands the same
  // Date instance through `cell.value` / `cell.result` and `extractCellText`.
  const timeFormatted = new WeakSet<Date>();

  worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    const rowData: unknown[] = [];
    row.eachCell({ includeEmpty: true }, (cell) => {
      const raw =
        cell.type === ExcelJS.ValueType.Formula
          ? cell.result ?? cell.value
          : cell.value;
      let value: unknown = extractCellText(raw);
      if (value === null) {
        const fallback = extractCellText(cell.text);
        if (typeof fallback === "string" && fallback.length > 0) {
          value = fallback;
        }
      }
      if (value instanceof Date && numFmtHasTime(cell.numFmt)) {
        timeFormatted.add(value);
      }
      rowData.push(value);
    });
    // jsonData[0] = Excel row 1 (header), jsonData[i] = Excel row i+1
    jsonData[rowNumber - 1] = rowData;
    if (rowData.length > maxColCount) maxColCount = rowData.length;
  });

  // Backfill any gaps that ExcelJS may have left as `undefined` slots
  for (let i = 0; i < jsonData.length; i++) {
    if (!jsonData[i]) jsonData[i] = new Array(maxColCount).fill(null);
  }

  if (jsonData.length <= 1) {
    return [];
  }

  // First row contains headers
  const headers = jsonData[0] as unknown[];

  // Find where the data ends (last non-empty row)
  let lastRowIndex = jsonData.length - 1;
  while (lastRowIndex > 0) {
    const row = jsonData[lastRowIndex] as unknown[];
    if (
      row &&
      row.some((cell) => cell !== null && cell !== undefined && cell !== "")
    ) {
      break;
    }
    lastRowIndex--;
  }

  // Create column-based structure
  const tableData: TableColumnData[] = [];

  // First pass: extract all column values
  for (let colIndex = 0; colIndex < headers.length; colIndex++) {
    const headerExtracted = extractCellText(headers[colIndex]);
    const header =
      typeof headerExtracted === "string" && headerExtracted.length > 0
        ? headerExtracted
        : `Column ${colIndex + 1}`;

    // Extract all values for this column (excluding header row)
    const values: (string | null)[] = [];
    for (let rowIndex = 1; rowIndex <= lastRowIndex; rowIndex++) {
      const row = jsonData[rowIndex] as unknown[];
      const cellValue = row && colIndex < row.length ? row[colIndex] : null;

      if (cellValue === null || cellValue === undefined) {
        values.push(null);
      } else if (cellValue instanceof Date) {
        values.push(
          excelDateCellToIso(cellValue, timeZone, timeFormatted.has(cellValue))
        );
      } else if (typeof cellValue === "string") {
        // Trim to match the CSV branch below, which has always trimmed. The
        // asymmetry was the bug: a spreadsheet column padded with a trailing
        // space (Excel/Word also emit NBSP) landed verbatim in the guest doc,
        // and `=` filters — whose operand IS trimmed on the way out — could
        // never match it again. Measured on prod: 8339 additionalFields values
        // and 594 standard fields, on field names that read as a spreadsheet
        // header row (Tag, Duplikate, Medium_langer_Titel).
        values.push(normalizeFieldValue(cellValue));
      } else if (typeof cellValue === "number" || typeof cellValue === "boolean") {
        values.push(String(cellValue));
      } else {
        const extracted = extractCellText(cellValue);
        values.push(
          extracted instanceof Date
            ? excelDateCellToIso(extracted, timeZone, timeFormatted.has(extracted))
            : extracted != null
              ? normalizeFieldValue(extracted)
              : null
        );
      }
    }

    tableData.push({
      header,
      values,
      invalidDetails: DataTypeDetectionService.getDataTypes(values),
    });
  }

  // Find the maximum number of values in any column
  const maxLength = Math.max(...tableData.map((col) => col.values.length));

  // Second pass: ensure all columns have the same number of values
  tableData.forEach((column) => {
    while (column.values.length < maxLength) {
      column.values.push(null);
    }
  });

  return tableData;
};

/**
 * Extract table data from a CSV file using PapaParse.
 * Assumes headers are in the first row and extracts all data rows.
 * Ensures all columns have the same number of values.
 */
export const extractTableDataCSV = async (
  file: File
): Promise<TableColumnData[]> => {
  const Papa = (await import("papaparse")).default;
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      complete: (results) => {
        try {
          const rows = results.data as string[][];

          if (rows.length <= 1) {
            resolve([]);
            return;
          }

          // First row contains headers
          const headers = rows[0].map((h) => (h || "").trim());

          // Find last non-empty row
          let lastRowIndex = rows.length - 1;
          while (lastRowIndex > 0) {
            const row = rows[lastRowIndex];
            if (row.some((cell) => (cell || "").trim() !== "")) {
              break;
            }
            lastRowIndex--;
          }

          // Create column-based data structure
          const tableData: TableColumnData[] = [];

          // First pass: extract all column values
          for (let colIndex = 0; colIndex < headers.length; colIndex++) {
            const header = headers[colIndex] || `Column ${colIndex + 1}`;

            // Extract all values for this column (excluding header row)
            const values: (string | null)[] = [];
            for (let rowIndex = 1; rowIndex <= lastRowIndex; rowIndex++) {
              const row = rows[rowIndex];
              const cellValue =
                colIndex < row.length
                  ? normalizeFieldValue(row[colIndex] || "")
                  : "";
              values.push(cellValue || null);
            }

            tableData.push({
              header,
              values,
              invalidDetails: DataTypeDetectionService.getDataTypes(values),
            });
          }

          // Find the maximum number of values in any column
          const maxLength = Math.max(
            ...tableData.map((col) => col.values.length)
          );

          // Second pass: ensure all columns have the same number of values
          tableData.forEach((column) => {
            while (column.values.length < maxLength) {
              column.values.push(null);
            }
          });

          resolve(tableData);
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => reject(error),
      skipEmptyLines: true,
    });
  });
};

/** File extensions the importer accepts. */
export function spreadsheetExtension(fileName: string): "xlsx" | "csv" | null {
  const ext = fileName.split(".").pop()?.toLowerCase();
  if (ext === "xlsx" || ext === "xls") return "xlsx";
  if (ext === "csv") return "csv";
  return null;
}

/**
 * Parse any supported spreadsheet File into the column-based table shape,
 * dispatching on the file extension. Returns [] for unsupported files.
 * `opts.timeZone` reaches the Excel branch only — CSV cells are text and
 * carry their own offset (the export writes ISO-8601 with `Z`).
 */
export async function parseSpreadsheetToTable(
  file: File,
  opts?: SpreadsheetParseOptions
): Promise<TableColumnData[]> {
  const kind = spreadsheetExtension(file.name);
  if (kind === "xlsx") return extractTableDataExcel(file, opts);
  if (kind === "csv") return extractTableDataCSV(file);
  return [];
}
